import React, { useRef } from "react";
import {
  CardNumberElement,
  CardCvcElement,
  CardExpiryElement,
} from "@stripe/react-stripe-js";
import "./CardSetup.styles.scss";
import { StripeElementChangeEvent } from "@stripe/stripe-js";
import { trackValidationError } from "../../../../../../helpers/Tracking";

interface CardSetupFormI {
  handleStripeStatus: (
    key: "error",
    value: StripeElementChangeEvent["error"],
    keySecond: "complete",
    valueSecond: StripeElementChangeEvent["complete"]
  ) => void;
  stripeInputStatus: {
    complete: StripeElementChangeEvent["complete"];
    error: StripeElementChangeEvent["error"];
  } | undefined
  translate: (key: string) => string;
}

const stripeFieldName = (elementType: string): string => {
  if (elementType.indexOf("Number") >= 0) return "card_number";
  if (elementType.indexOf("Expiry") >= 0) return "card_expiry";
  if (elementType.indexOf("Cvc") >= 0) return "card_cvc";
  return elementType;
};

const CardSetupForm: React.FC<CardSetupFormI> = ({
  handleStripeStatus,
  stripeInputStatus,
  translate,
}) => {
  const errorRef = useRef<Record<string, string | undefined>>({});

  const onStripeChange = (e: StripeElementChangeEvent) => {
    const field = stripeFieldName(e.elementType);
    // Error de formato de Stripe Elements (número, caducidad o CVC): uno por
    // cambio de mensaje, con el `code` corto de Stripe (incomplete_number,
    // invalid_expiry_year_past…); nunca lo tecleado ni el texto del error.
    if (e.error) {
      if (errorRef.current[field] !== e.error.message) {
        errorRef.current[field] = e.error.message;
        trackValidationError(field, "format", e.error.code ?? null);
      }
    } else if (errorRef.current[field]) {
      errorRef.current[field] = undefined;
    }

    handleStripeStatus("error", e.error, "complete", e.complete);
  };

  return (
    <div className="card-form">
      <div className="flex flex-col pb-7">
        <p className="text-question-small font-bold  max-w-[740px] md:text-center"></p>
        <div className="w-full">
          <label htmlFor="Nombre" className="font-normal">
            {translate("payment.cardSetup.name")}
          </label>
          <CardNumberElement
            options={{
              showIcon: true,
            }}
            className="cardNumber--input mt-2"
            onChange={onStripeChange}
          />
          {stripeInputStatus?.error && stripeInputStatus.error.code === 'incomplete_number' && (
            <p className="text-red text-[13px] pt-[10px] text-left self-start">
              {stripeInputStatus?.error.message}
            </p>
          )}
        </div>
      </div>
      <div className="card--row2">
        <div className="flex flex-col pb-7">
          <p className="text-question-small font-bold  max-w-[740px] md:text-center"></p>
          <div className="w-full">
            <label htmlFor="Nombre" className="font-normal">
              {translate("payment.cardSetup.expire")}
            </label>
            <CardExpiryElement
              className="cardNumber--input mt-2"
              onChange={onStripeChange}
            />
            {stripeInputStatus?.error && stripeInputStatus.error.code === 'incomplete_expiry' && (
              <p className="text-red text-[13px] pt-[10px] text-left self-start">
                {stripeInputStatus?.error.message}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-col pb-7">
          <p className="text-question-small font-bold  max-w-[740px] md:text-center"></p>
          <div className="w-full">
            <label htmlFor="Nombre" className="font-normal">
              {translate("payment.cardSetup.cvc")}
            </label>
            <CardCvcElement
              className="cardNumber--input mt-2"
              onChange={onStripeChange}
            />
            {stripeInputStatus?.error && stripeInputStatus.error.code === 'incomplete_cvc' && (
              <p className="text-red text-[13px] pt-[10px] text-left self-start">
                {stripeInputStatus?.error.message}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardSetupForm;
